export type MachineFamilySelection = 'none' | 'include' | 'exclude';
export type MachineFamilySelections = Record<string, MachineFamilySelection>;

export type MachineFamilyFilter = { filterType: string; value: string; include: boolean };

/** Stable browsing identity, independent of dropdown option ordering. */
export function machineFamilyFilterKey(filters: MachineFamilyFilter[]): string {
  return JSON.stringify(
    filters
      .filter((filter) => filter.filterType === 'MACHINE_FAMILY')
      .map(({ value, include }) => [value, include] as const)
      .sort(([a, ai], [b, bi]) => a.localeCompare(b) || Number(ai) - Number(bi))
  );
}

/**
 * True when the selections contain both an included and an excluded machine
 * family. Mixing the two doesn't make sense: the scope is either an allow-list
 * or a deny-list, so this is used to block saving and to surface a message.
 */
export function hasMixedMachineFamilySelections(
  selections: MachineFamilySelections | undefined | null
): boolean {
  if (!selections) return false;
  const values = Object.values(selections);
  return values.includes('include') && values.includes('exclude');
}

export function filtersToMachineFamilySelections(
  filters: MachineFamilyFilter[] | undefined | null
): MachineFamilySelections {
  const result: MachineFamilySelections = {};
  if (!filters) return result;
  for (const filter of filters) {
    if (filter.filterType === 'MACHINE_FAMILY') {
      result[filter.value] = filter.include ? 'include' : 'exclude';
    }
  }
  return result;
}

/**
 * Appends machine-family scope filters to a URLSearchParams as the repeated
 * `includeMachineFamilies` / `excludeMachineFamilies` query parameters understood
 * by the scope-tree GET endpoints.
 */
export function appendMachineFamilyFilterParams(
  params: URLSearchParams,
  filters: MachineFamilyFilter[] | undefined | null
): void {
  if (!filters) return;
  for (const filter of filters) {
    if (filter.filterType !== 'MACHINE_FAMILY') continue;
    params.append(
      filter.include ? 'includeMachineFamilies' : 'excludeMachineFamilies',
      filter.value
    );
  }
}

export function machineFamilySelectionsToFilters(
  selections: MachineFamilySelections
): { filterType: 'MACHINE_FAMILY'; value: string; include: boolean }[] {
  const filters: { filterType: 'MACHINE_FAMILY'; value: string; include: boolean }[] = [];
  for (const [family, selection] of Object.entries(selections)) {
    if (selection === 'include' || selection === 'exclude') {
      filters.push({
        filterType: 'MACHINE_FAMILY',
        value: family,
        include: selection === 'include'
      });
    }
  }
  return filters;
}
